import { useContext } from 'react';
import {
  defaultFilterData,
  FilterData,
  AreaKey,
  RowsDataUseProps,
  RowsDataContext,
  defaultRowsData,
  RowsData,
  defaultPaginationData,
  RowsDataWatchProps,
  ColumnInfo,
  FieldValuesFilterData,
} from './types';
import {
  deserializeFilter,
  FilterDef,
  filtersComputeFormInitialValues,
  filtersComputeQueryFilter,
  filtersIsFieldValueFilled,
  filterUrlParamName,
} from '../filter/Filters';
import { ElementOf, tuple } from '../common/props';
import { FilterType } from '../filter/types';
import { useHistory, useLocation } from 'react-router';
import { DataSortVO } from '@mc/data-filter-react';
import { DataAggregateVO, DataGroupVO } from '../final-table/FinalTableProps';

export const InitFilterSources = tuple('urlSearch', 'formFilterParam');
export type InitFilterSource = ElementOf<typeof InitFilterSources>;

export type RowsDataConfig = {
  /**
   * Výchozí zobrzazení položek filtru.
   */
  initialVisibleFields?: string[];
  initialFilter?: {
    /*
     * Deprecated, filtr nyní automaticky bere oba 2 zdroje, přednost má url
     * @deprecated filtr nyní automaticky bere oba 2 zdroje, přednost má url
     */
    source?: InitFilterSource;
    filterType?: FilterType;
    filterDefs: FilterDef[];
    formFilterParam?: any;
  };
  initialShowFooter?: boolean;
  initialSort?: DataSortVO[];
  initialGrouping?: DataGroupVO[];
  initialAggregate?: DataAggregateVO[];
  initialColumns?: ColumnInfo[];
  initialGroupColumnWidth?: number;
  initialFieldValuesFilter?: FieldValuesFilterData;
};

export const getRowsDataDataKey = (kk: string | readonly unknown[]) => {
  let key: string;
  if (Array.isArray(kk)) {
    key = kk.map((x) => JSON.stringify(x)).join('|');
  } else {
    key = `${kk}`;
  }
  return key;
};

export const useRowsDataWatch = <ID extends unknown = {}>(
  filterKey: AreaKey
): RowsDataWatchProps<ID> => {
  const rowsDataContext = useContext(RowsDataContext);
  if (!rowsDataContext.initByProvider) {
    throw new Error(
      'Použití useRowsData vyžaduje obalení aplikace pomací RowsDataProvider'
    );
  }
  let key = getRowsDataDataKey(filterKey);

  let rowsData: RowsData<ID> = rowsDataContext.dataMap[key]?.rowsData;

  return {
    rows: {
      checkedIds: rowsData?.checkedIds ?? [],
      setCheckedIds: (ids) => {
        rowsDataContext.setCheckedIds(key, ids);
      },
    },
  };
};

export const useRowsData = <
  FILTER extends unknown = {},
  ID extends unknown = {},
>(
  filterKey: AreaKey,
  config?: RowsDataConfig
): RowsDataUseProps<FILTER, ID> => {
  const rowsDataContext = useContext(RowsDataContext);
  if (!rowsDataContext.initByProvider) {
    throw new Error(
      'Použití useRowsData vyžaduje obalení aplikace pomací RowsDataProvider'
    );
  }

  let key = getRowsDataDataKey(filterKey);

  let history = useHistory();

  let reinitialize = false;

  // Init Filters
  let filterData: FilterData = rowsDataContext.dataMap[key]?.filterData;
  if (!filterData) {
    filterData = { ...defaultFilterData };

    if (config?.initialVisibleFields) {
      filterData.visibleFields = config.initialVisibleFields;
    }
    reinitialize = true;
  }

  // Init Rows data
  let rowsData: RowsData<ID> = rowsDataContext.dataMap[key]?.rowsData;
  if (!rowsData?.initializedFromConfig) {
    rowsData = { ...defaultRowsData, ...rowsData, initializedFromConfig: true };

    if (config?.initialSort) {
      rowsData.sort = config.initialSort;
    }
    if (config?.initialGrouping) {
      rowsData.grouping = config.initialGrouping;
    }
    if (config?.initialAggregate) {
      rowsData.aggregate = config.initialAggregate;
    }
    if (config?.initialColumns) {
      rowsData.columns = config.initialColumns;
    }
    if (config?.initialGroupColumnWidth !== undefined) {
      rowsData.groupColumnWidth = config.initialGroupColumnWidth;
    }
    if (config?.initialShowFooter !== undefined) {
      rowsData.showFooter = config.initialShowFooter;
    }

    if (config?.initialFieldValuesFilter) {
      filterData = {
        ...filterData,
        fieldValuesFilter: config.initialFieldValuesFilter,
      };
    }

    reinitialize = true;
  }

  // Init pagination
  let paginationData = rowsDataContext.dataMap[key]?.paginationData;
  if (!paginationData) {
    paginationData = { ...defaultPaginationData };
    reinitialize = true;
  }

  let location = useLocation();
  let searchParams = new URLSearchParams(location.search);
  let finalFormFilter: any = undefined;

  let filterReinitialized = false;
  if (searchParams.has(filterUrlParamName)) {
    const val = searchParams.get(filterUrlParamName);
    const deserializeDFilterData = deserializeFilter(val);
    const formFilter = deserializeDFilterData?.filter;
    const refetch = deserializeDFilterData?.options?.refetch;
    finalFormFilter = formFilter || {};
    filterData = { ...filterData };
    filterData.formFilter = finalFormFilter;
    filterData.queryFilter = filtersComputeQueryFilter(
      config?.initialFilter?.filterType || 'default',
      finalFormFilter,
      config?.initialFilter?.filterDefs || []
    );
    filterData.lastFilterChange = refetch
      ? new Date().getMilliseconds()
      : filterData.lastFilterChange;
    if (
      refetch &&
      rowsDataContext?.dataMap[key]?.rowsData?.checkedIds?.length > 0
    ) {
      rowsDataContext.setCheckedIds(key, []);
    }
    reinitialize = true;
    filterReinitialized = true;

    // Clear url filter
    history.replace({ search: undefined });
  } else if (config?.initialFilter) {
    if (
      !filterData.initializedFromConfig &&
      Object.keys(filterData?.queryFilter)?.length === 0 &&
      JSON.stringify(filterData.formFilterParam) !==
        JSON.stringify(config.initialFilter.formFilterParam)
    ) {
      filterData = { ...filterData, initializedFromConfig: true };
      // Reinit při změně
      filterData.formFilterParam = config.initialFilter.formFilterParam;
      finalFormFilter = filtersComputeFormInitialValues(
        config.initialFilter.filterType || 'default',
        config.initialFilter?.formFilterParam || {},
        config.initialFilter.filterDefs
      );

      filterData.formFilter = finalFormFilter;
      filterData.queryFilter = filtersComputeQueryFilter(
        config.initialFilter.filterType || 'default',
        finalFormFilter,
        config.initialFilter.filterDefs
      );
      reinitialize = true;
      filterReinitialized = true;
    }
  }

  // Pokud je ve filtru použita nějaká hodnota a daný field není v seznamu viditelných, "zviditelníme ho"
  if (
    filterReinitialized &&
    finalFormFilter &&
    config?.initialFilter?.filterDefs &&
    filterData.visibleFields !== undefined
  ) {
    // undefined visible fields = všechny jsou viditelné
    config.initialFilter.filterDefs.forEach((filterDef) => {
      if (filterData.visibleFields?.indexOf(filterDef.name) === -1) {
        if (
          filtersIsFieldValueFilled(
            config.initialFilter?.filterType || 'default',
            finalFormFilter,
            filterDef
          )
        ) {
          filterData.visibleFields = [
            ...filterData.visibleFields,
            filterDef.name,
          ];
        }
      }
    });
  }

  // Zde se jedná o první inicializaci a nemělo by vadit, že takto napřímo modifikujeme kontext - zde to takto i chceme
  if (reinitialize) {
    rowsDataContext.dataMap[key] = {
      filterData,
      rowsData,
      paginationData,
    };
  }

  return {
    columns: {
      columns: rowsData.columns,
      setColumns: (columns) => {
        rowsDataContext.setColumns(key, columns);
      },
    },
    footer: {
      setShowFooter: (showFooter) => {
        rowsDataContext.setShowFooter(key, showFooter);
        showFooter: rowsData.showFooter;
      },
      showFooter: rowsData.showFooter,
    },
    filter: {
      formFilter: filterData.formFilter,
      queryFilter: filterData.queryFilter,
      fieldValuesFilter: filterData.fieldValuesFilter,
      lastFilterChange: filterData.lastFilterChange,
      initialVisibleFields: config?.initialVisibleFields,
      visibleFields: filterData.visibleFields,
      setFilter: (newFormFilter, newQueryFilter) => {
        // @ts-ignore
        rowsDataContext.setFilter(key, newFormFilter, newQueryFilter);
      },
      getClearFilter: (ccf) => {
        let newFormFilter: any = undefined;
        if (ccf.initialFilter) {
          newFormFilter = filtersComputeFormInitialValues(
            ccf.type || 'default',
            config?.initialFilter?.formFilterParam || {},
            ccf.filtersDef
          );
        } else {
          newFormFilter = filtersComputeFormInitialValues(
            ccf.type || 'default',
            {},
            ccf.filtersDef
          );
        }
        return newFormFilter;
      },
      setFieldValuesFilter: (fieldValuesFilter) => {
        rowsDataContext.setFieldValuesFilter(key, fieldValuesFilter);
      },
      setVisibleFields: (fields) => {
        rowsDataContext.setVisibleFields(key, fields);
      },
    },
    grouping: {
      grouping: rowsData.grouping,
      setGrouping: (grouping) => {
        rowsDataContext.setGrouping(key, grouping);
      },
      aggregate: rowsData.aggregate,
      setAggregate: (aggregate) => {
        rowsDataContext.setAggregate(key, aggregate);
      },
      groupColumnWidth: rowsData.groupColumnWidth,
      setGroupColumnWidth: (groupWidth) => {
        rowsDataContext.setGroupColumnWidth(key, groupWidth);
      },
    },
    rows: {
      scrollTop: rowsData.scrollTop,
      checkedIds: rowsData.checkedIds,
      setCheckedIds: (ids) => {
        rowsDataContext.setCheckedIds(key, ids);
      },
      sort: rowsData.sort,
      setSort: (sort) => {
        rowsDataContext.setSort(key, sort);
      },
      setScrollTop: (scrollTop) => {
        rowsDataContext.setScrollTop(key, scrollTop);
      },
      lastVisitedRowIndex: rowsDataContext.dataMapLastVisitedRowIndex[key],
      setLastVisitedRowIndex: (id) => {
        rowsDataContext.setLastVisitedRowIndex(key, id);
      },
    },
    pagination: {
      pageSize: paginationData.pageSize,
      page: paginationData.page,
      setPage: (page) => {
        rowsDataContext.setPage(key, page);
        rowsData.checkedIds = [];
      },
    },
  };
};
